import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, db, googleProvider } from '../../firebase/config';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  checkActionCode,
  signOut
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { isIOSDevice, appHandoffUrl } from '../../utils/appHandoff';
import { getDeviceId } from '../../utils/deviceId';
import '../styles/auth.css';

// Email-link (magic link) sign-in is the primary email flow (WEB-Q9
// relaunch, Lily 2026-09-01): one "Continue with email" works for both new
// and existing accounts, the click on the emailed link IS the verification,
// and there are no password/verification/re-register states to maintain.
// Password login stays available behind "Have a password?" for existing
// email/password members.
const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';

// localStorage throws in storage-blocked contexts (Safari "Block all
// cookies", some private modes). A throw here must never crash the render
// or turn a SUCCESSFUL send into an error message (fresh-eye 2026-09-03) -
// the flows all degrade correctly on null (cross-device prompt appears,
// heuristic falls back).
const safeStorageGet = (key) => { try { return window.localStorage.getItem(key); } catch (_) { return null; } };
const safeStorageSet = (key, value) => { try { window.localStorage.setItem(key, value); } catch (_) { /* degrade */ } };
const safeStorageRemove = (key) => { try { window.localStorage.removeItem(key); } catch (_) { /* degrade */ } };

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // When the page is opened FROM an emailed sign-in link, show the
  // completing state immediately - rendering the normal sign-in form for
  // the few seconds the link takes to verify confused testers into
  // closing/refreshing (Lily 2026-09-01).
  const [view, setView] = useState(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return 'main';
    // IOS-Q9 (sketch signed off 2026-09-03, refined per Lily same day):
    // the interstitial is for APP-requested links only. The link's own
    // src stamp (set by sendMagicLink from the requesting platform) is
    // authoritative in any browser; links without it fall back to the
    // local heuristic - a stored emailForSignIn means THIS browser
    // requested the link. Android and desktop never see the interstitial.
    const src = new URLSearchParams(window.location.search).get('src');
    const webOrigin = src === 'web' || (src !== 'ios' && !!safeStorageGet(EMAIL_FOR_SIGN_IN_KEY));
    return isIOSDevice() && !webOrigin ? 'appHandoff' : 'completing';
  }); // main | completing | linkSent | expired | reset | appHandoff
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const completingLink = useRef(false);
  // IOS-Q9 interstitial: on iOS the link is NOT consumed on mount - the
  // user first chooses app vs browser; this holds the deferred completion.
  const pendingLinkRun = useRef(null);
  const navigate = useNavigate();

  const getErrorMessage = (error) => {
    // Callable-function errors (code "functions/...") carry OUR OWN
    // user-facing message from the HttpsError - show it verbatim. The
    // generic fallback here hid the rate-limit explanation ("Too many
    // requests for this address. Please check your inbox.") and made a
    // throttled send look broken (Lily's report, 2026-09-03).
    // functions/internal is the one exception: an UNHANDLED server error
    // carries the literal message "internal", which is not user copy -
    // let it fall through to the generic text (fresh-eye 2026-09-03).
    if (typeof error.code === 'string' && error.code.startsWith('functions/')
        && error.code !== 'functions/internal' && error.message) {
      return error.message;
    }
    switch (error.code) {
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please try again.';
      case 'auth/user-not-found':
        return 'No account found with this email. Please sign up first.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use at least 6 characters.';
      case 'auth/invalid-email':
        return 'Invalid email address. Please check and try again.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/invalid-action-code':
      case 'auth/expired-action-code':
        return 'This sign-in link has expired or was already used. Enter your email below and we will send you a fresh one.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with the same email but different sign-in method.';
      case 'auth/cancelled-popup-request':
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled. Please try again.';
      case 'auth/operation-not-allowed':
        return 'This sign-in method is not enabled. Please contact support.';
      case 'auth/popup-blocked':
        return 'Popup was blocked by your browser. Please allow popups and try again.';
      case 'auth/unauthorized-domain':
        return 'This domain is not authorized for authentication.';
      default:
        return 'An error occurred. Please try again.';
    }
  };

  // Create the member's users doc on FIRST verified sign-in (full shape,
  // createdAt from the Auth record), or merge the sign-in stamp on
  // returning visits. Retried briefly: right after sign-in the new session
  // can take a moment to propagate to security-rule evaluation.
  const ensureUserDoc = async (user) => {
    const ref = doc(db, 'users', user.uid);
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const snapshot = await getDoc(ref);
        if (snapshot.exists()) {
          await setDoc(ref, {
            email: user.email,
            emailVerified: true,
            lastSignInTime: serverTimestamp()
          }, { merge: true });
        } else {
          await setDoc(ref, {
            email: user.email,
            name: user.displayName || '',
            points: 0,
            coins: 0,
            stamps: 0,
            emailVerified: true,
            lastSignInTime: serverTimestamp(),
            createdAt: user.metadata?.creationTime
              ? new Date(user.metadata.creationTime)
              : serverTimestamp()
          });
        }
        return;
      } catch (writeError) {
        if (attempt === maxAttempts) throw writeError;
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
  };

  // Complete a magic-link sign-in when the page is opened FROM the emailed
  // link. The email normally comes from localStorage (same browser that
  // requested the link); on a different device the user confirms it once.
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    if (completingLink.current) return;
    completingLink.current = true;

    const run = async () => {
      // Validate the code BEFORE asking the user anything: a reused or
      // expired link goes straight to a clear explanation with a one-tap
      // "send me a new link" - never a confusing email prompt (Lily's
      // device-test feedback 2026-09-01). checkActionCode does not consume
      // the code, and for sign-in links it usually returns the target
      // email, which also removes the cross-device prompt.
      const oobCode = new URLSearchParams(window.location.search).get('oobCode');
      let codeEmail = null;
      try {
        const info = await checkActionCode(auth, oobCode);
        codeEmail = (info && info.data && info.data.email) || null;
      } catch (error) {
        // A NETWORK failure is not a used/expired link - the expired
        // view's copy would falsely claim the link is dead and push the
        // user to burn a re-request (fresh-eye 2026-09-03). Honest copy,
        // back to main; the link stays valid to tap again.
        if (error.code === 'auth/network-request-failed') {
          completingLink.current = false;
          setMessage('Could not reach the sign-in service. Check your connection and tap the link in your email again.');
          setView('main');
          return;
        }
        const stored = safeStorageGet(EMAIL_FOR_SIGN_IN_KEY);
        if (stored) setEmail(stored);
        setView('expired');
        return;
      }
      let linkEmail = codeEmail || safeStorageGet(EMAIL_FOR_SIGN_IN_KEY);
      if (!linkEmail) {
        linkEmail = window.prompt('Please confirm the email address this sign-in link was sent to:');
      }
      if (!linkEmail) {
        setMessage('Sign-in was cancelled. Enter your email below to get a new link.');
        setView('main');
        return;
      }
      setBusy(true);
      try {
        const userCredential = await signInWithEmailLink(auth, linkEmail, window.location.href);
        safeStorageRemove(EMAIL_FOR_SIGN_IN_KEY);
        try {
          await ensureUserDoc(userCredential.user);
        } catch (firestoreError) {
          setMessage('Signed in, but there was an issue with user data. Some features may not work properly.');
        }
        navigate('/dashboard');
      } catch (error) {
        setBusy(false);
        if (error.code === 'auth/invalid-action-code' || error.code === 'auth/expired-action-code') {
          setEmail(linkEmail);
          setView('expired');
        } else {
          setMessage(getErrorMessage(error));
          setView('main');
        }
      }
    };
    const src = new URLSearchParams(window.location.search).get('src');
    const webOrigin = src === 'web' || (src !== 'ios' && !!safeStorageGet(EMAIL_FOR_SIGN_IN_KEY));
    if (isIOSDevice() && !webOrigin) {
      // App-requested (or foreign-browser) link on iOS: deferred until
      // "Continue in browser" - "Open in the Lucky Claww app" hands the
      // untouched link to the app instead
      pendingLinkRun.current = run;
    } else {
      run();
    }
    // Intentionally mount-only: the sign-in link is consumed exactly once.
  }, []); // eslint-disable-line

  const handleSendLink = async (e) => {
    e.preventDefault();
    if (!email) {
      setMessage('Please enter your email address');
      return;
    }
    setBusy(true);
    try {
      if (process.env.REACT_APP_ENVIRONMENT === 'emulator') {
        // Emulator harness runs auth+firestore only - use the SDK sender
        // (the auth emulator captures the link via its oobCodes endpoint)
        await sendSignInLinkToEmail(auth, email, {
          url: `${window.location.origin}/auth?src=web`,
          handleCodeInApp: true
        });
      } else {
        // Production: our own branded, Pacific-time, Gmail-delivered email
        // (functions/src/signInEmail.ts) - the Firebase default template is
        // UTC-stamped and spam-prone (Lily 2026-09-01)
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        await httpsCallable(getFunctions(), 'sendMagicLink')({ email, source: 'web', deviceId: getDeviceId() });
      }
      safeStorageSet(EMAIL_FOR_SIGN_IN_KEY, email);
      setMessage('');
      setView('linkSent');
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (!userCredential.user.emailVerified) {
        // Best-effort re-send before the enforced sign-out, so the user is
        // never stuck: signInWithEmailAndPassword persists a session even
        // for unverified accounts, which would otherwise let the user
        // reach /dashboard directly. Dashboard has a matching guard.
        try { await sendEmailVerification(userCredential.user); } catch { /* best-effort */ }
        await signOut(auth);
        setMessage('Please verify your email before logging in. We just re-sent the verification link - check your inbox. Tip: "Continue with email" above signs you in without a password.');
        return;
      }
      await ensureUserDoc(userCredential.user);
      navigate('/dashboard');
    } catch (error) {
      setMessage(getErrorMessage(error));
    }
  };

  const handleGoogleAuth = async () => {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      try {
        await ensureUserDoc(userCredential.user);
      } catch (firestoreError) {
        setMessage('Authentication successful, but there was an issue with user data. Some features may not work properly.');
      }
      navigate('/dashboard');
    } catch (error) {
      setMessage(getErrorMessage(error));
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setMessage('Please enter your email address');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Password reset email sent! Please check your inbox.');
      setView('main');
    } catch (error) {
      setMessage(getErrorMessage(error));
    }
  };

  const header = (
    <div className="auth-header">
      <img src="/lc-logo.png" alt="Lucky Claww Logo" />
      <h1>Lucky Claww Membership System</h1>
    </div>
  );

  if (view === 'reset') {
    return (
      <div className="auth-container">
        <div className="auth-content">
          {header}
          <form onSubmit={handleResetPassword} className="auth-form">
            <h2>Reset Password</h2>
            <div className="form-group">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {message && <p className="message">{message}</p>}
            <button type="submit">Send Reset Link</button>
            <p>
              <a href="#" onClick={(e) => { e.preventDefault(); setView('main'); setMessage(''); }}>
                Back
              </a>
            </p>
          </form>
        </div>
      </div>
    );
  }

  if (view === 'appHandoff') {
    // A definitive app-origin stamp means the requester HAS the app - no
    // browser option offered (Lily 2026-09-03). Links without the stamp
    // (pre-deploy emails, foreign browsers) keep the escape hatch.
    const definitiveAppOrigin = new URLSearchParams(window.location.search).get('src') === 'ios';
    return (
      <div className="auth-container">
        <div className="auth-content">
          {header}
          <div className="auth-box">
            <h2>Finish signing in</h2>
            <a className="app-handoff-btn" href={appHandoffUrl(window.location.href)}>
              Open in the Lucky Claww app
            </a>
            {!definitiveAppOrigin && (
              <button
                onClick={() => { setView('completing'); if (pendingLinkRun.current) pendingLinkRun.current(); }}
                className="google-btn"
                style={{ width: '100%', marginTop: 12, padding: '14px' }}
              >
                <span>Continue in browser</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'completing') {
    return (
      <div className="auth-container">
        <div className="auth-content">
          {header}
          <div className="auth-box">
            <div className="auth-spinner" aria-hidden="true"></div>
            <h2>Signing you in…</h2>
            <p className="signup-recommendation">
              One moment - we are confirming your sign-in link.
              Please keep this tab open.
            </p>
            {message && <div className="auth-message">{message}</div>}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'expired') {
    return (
      <div className="auth-container">
        <div className="auth-content">
          {header}
          <div className="auth-box">
            <h2>This sign-in link was already used</h2>
            <p className="signup-recommendation">
              Sign-in links work once, so a link that already signed you in
              (or has expired) cannot be used again. No problem - we can
              email you a fresh one right now.
            </p>
            {message && <div className="auth-message">{message}</div>}
            <form onSubmit={handleSendLink}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="email-link-btn" disabled={busy} style={{ width: '100%', marginTop: 12, padding: '14px' }}>
                {busy ? 'Sending…' : 'Email me a new sign-in link'}
              </button>
            </form>
            <div className="auth-toggle-section">
              <p className="auth-toggle-text">
                Already signed in on this device?{' '}
                <span onClick={() => navigate('/dashboard')} className="auth-link">
                  Go to your dashboard
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'linkSent') {
    return (
      <div className="auth-container">
        <div className="auth-content">
          {header}
          <div className="auth-box">
            <h2>Check your inbox</h2>
            <p className="signup-recommendation">
              We sent a sign-in link to <strong>{email}</strong>.
              Open it on this device to sign in. If you don't see it,
              please check your spam or junk folder.
            </p>
            {message && <div className="auth-message">{message}</div>}
            <button onClick={handleSendLink} className="google-btn" disabled={busy}>
              <span>Resend email</span>
            </button>
            <div className="auth-toggle-section">
              <p className="auth-toggle-text">
                Wrong email?{' '}
                <span onClick={() => { setView('main'); setMessage(''); }} className="auth-link">
                  Go back
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-content">
        <Link to="/" className="home-link-subtle">
          ← main page
        </Link>
        {header}
        <div className="auth-box">
          <h2>Welcome to Lucky Claww</h2>

          <div className="signup-recommendation">
            Join the Lucky Claww family to track points, get stamps, and redeem giant plushies!
            One click below signs you up or logs you in.
          </div>

          {message && <div className="auth-message">{message}</div>}

          <button onClick={handleGoogleAuth} className="google-btn">
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              className="google-icon"
            />
            <span>Continue with Google</span>
          </button>

          <div className="email-signin-section">
            <div className="or-divider">
              <span>or continue with email</span>
            </div>
            <form onSubmit={handleSendLink}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="email-link-btn" disabled={busy}>
                {busy ? 'Sending…' : 'Continue with email'}
              </button>
            </form>
            <p className="auth-toggle-text email-link-hint">
              We will email you a sign-in link - no password needed.
            </p>
          </div>

          <div className="auth-toggle-section">
            <p className="auth-toggle-text">
              Have a password?{' '}
              <span
                onClick={() => { setShowPasswordLogin(!showPasswordLogin); setMessage(''); }}
                className="auth-link"
              >
                Log in with password
              </span>
            </p>
          </div>

          {showPasswordLogin && (
            <div className="email-signin-section">
              <form onSubmit={handlePasswordLogin}>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div className="forgot-password">
                  <a href="#" onClick={(e) => { e.preventDefault(); setView('reset'); setMessage(''); }}>
                    Forgot Password?
                  </a>
                </div>
                <button type="submit">Login</button>
              </form>
            </div>
          )}

          <div className="apple-note">
            <p>Want to sign in with Apple? Download our iOS app from the App Store!</p>
            <a
              href="https://apps.apple.com/us/app/lucky-claww/id6749842659"
              target="_blank"
              rel="noopener noreferrer"
              className="app-store-link"
            >
              Download iOS App
            </a>
          </div>

          <div className="legal-links">
            <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">
              Terms of Use
            </a>
            <span className="legal-separator">|</span>
            <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
